import { useEffect, useState } from 'react';
import { useAppSelector } from 'mastodon/store';

export function firstDraftUrl(text) {
  const match = text.match(/https?:\/\/[^\s<>]+/i);
  if (!match) return null;
  const candidate = match[0].replace(/[.,!?:;]+$/, '').replace(/\)+$/, (ending) => {
    const opens = (match[0].match(/\(/g) || []).length;
    const closes = (match[0].match(/\)/g) || []).length;
    return ending.slice(0, Math.max(0, ending.length - (closes - opens)));
  });
  try {
    const url = new URL(candidate);
    return url.username || url.password ? null : url.href;
  } catch { return null; }
}

export function DraftLinkPreview({ text, suppressed = false }) {
  const quoted = useAppSelector((state) => !!state.compose.get('quoted_status_id'));
  const poll = useAppSelector((state) => !!state.compose.get('poll'));
  const direct = useAppSelector((state) => state.compose.get('privacy') === 'direct');
  const url = !suppressed && !quoted && !poll && !direct ? firstDraftUrl(text) : null;
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    setPreview(null);
    if (!url) return;
    const controller = new AbortController();
    let pollTimer;
    const fetchCard = async (cachedOnly = false) => {
      const response = await fetch('/driscoll/link-preview', {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
        body: JSON.stringify({ url, ...(cachedOnly ? { cached_only: true } : {}) }),
      });
      if (!response.ok) throw new Error('Unavailable');
      return response.json();
    };
    const updateThumbnail = async (remaining) => {
      if (controller.signal.aborted) return;
      try {
        const card = await fetchCard(true);
        if (controller.signal.aborted) return;
        setPreview({ url, card });
        if (card.pending && remaining > 0) pollTimer = setTimeout(() => updateThumbnail(remaining - 1), 2000);
      } catch { /* Keep the metadata card if background processing is unavailable. */ }
    };
    const timer = setTimeout(async () => {
      setPreview({ url, loading: true });
      try {
        const card = await fetchCard();
        if (!controller.signal.aborted) {
          setPreview({ url, card });
          if (card.pending) pollTimer = setTimeout(() => updateThumbnail(14), 2000);
        }
      } catch {
        if (!controller.signal.aborted) setPreview({ url, unavailable: true });
      }
    }, 800);
    return () => { clearTimeout(timer); clearTimeout(pollTimer); controller.abort(); };
  }, [url]);
  if (!url || preview?.url !== url) return null;
  if (preview.loading) return <div className='dr-draft-preview-state' role='status'>Loading link preview…</div>;
  if (preview.unavailable) return <div className='dr-draft-preview-state' role='status'>Preview unavailable. You can still post this link.</div>;
  const { card } = preview;
  return <aside className='dr-draft-preview' aria-label='Link preview'>
    {card.image && <img src={card.image} alt={card.image_description || ''} />}
    <div><small>{card.provider_name || new URL(url).hostname}</small>
      <strong>{card.title || new URL(url).hostname}</strong>
      {card.description && <p>{card.description}</p>}
    </div>
  </aside>;
}
